import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, X, MapPin, Calendar, Clock, Users, Info, Search, Send } from 'lucide-react';
import Logo from '../../components/Logo';
import Footer from '../../components/Footer';
import WalletMenu from '../../components/WalletMenu';
import NavigationMenu from '../../components/NavigationMenu';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';

interface Helicopter {
  id: number;
  name: string;
  type: string;
  capacity: string;
  range: string;
  speed: string;
  price: string;
  location: string;
  image: string;
}

interface HelicopterDetailModalProps {
  helicopter: Helicopter;
  onClose: () => void;
}

const HelicopterDetailModal: React.FC<HelicopterDetailModalProps> = ({ helicopter, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In a real application, you would send this data to your backend
      console.log('Sending helicopter charter request to bookings@privatecharterx.com', {
        formData,
        helicopter
      });
      
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
        <div className="min-h-screen py-10">
          <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header with close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full text-gray-600 hover:text-gray-900 z-10"
            >
              <X size={24} />
            </button>

            {/* Hero Image */}
            <div className="relative h-80">
              <img 
                src={helicopter.image} 
                alt={helicopter.name} 
                className="w-full h-full object-cover"
              />
            </div>

            {/* Content */}
            <div className="p-6 md:p-10">
              <div className="flex flex-col md:flex-row gap-8">
                {/* Left Column */}
                <div className="w-full md:w-2/3">
                  <h1 className="text-3xl font-bold mb-4">{helicopter.name}</h1>
                  
                  <div className="flex items-center text-gray-700 mb-6">
                    <MapPin size={18} className="mr-2" />
                    <span className="font-medium">{helicopter.location}</span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Type</div>
                      <div className="font-medium">{helicopter.type}</div>
                    </div>
                    
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Capacity</div>
                      <div className="font-medium">{helicopter.capacity}</div>
                    </div>
                    
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Range</div>
                      <div className="font-medium">{helicopter.range}</div>
                    </div>
                    
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Speed</div>
                      <div className="font-medium">{helicopter.speed}</div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-bold mb-4">About This Helicopter</h2>
                    <p className="text-gray-600">
                      Experience the ultimate in aerial luxury with the {helicopter.name}. This {helicopter.type} offers exceptional performance and comfort for up to {helicopter.capacity}, making it perfect for city transfers, sightseeing tours, and special events.
                      
                      With a range of {helicopter.range} and a cruising speed of {helicopter.speed}, this helicopter provides efficient transportation while offering stunning aerial views. The spacious cabin features premium leather seating, climate control, and noise-cancelling headsets for all passengers.
                      
                      Currently based in {helicopter.location}, this helicopter is available for charter throughout the region, with experienced pilots who prioritize safety and comfort.
                    </p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold mb-4">What's Included</h2>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <ArrowRight size={18} className="text-black flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">Professional pilot with extensive experience</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight size={18} className="text-black flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">Noise-cancelling headsets for all passengers</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight size={18} className="text-black flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">Complimentary refreshments</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight size={18} className="text-black flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">Personalized flight route</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight size={18} className="text-black flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">Ground transportation coordination</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right Column - Booking Form */}
                <div className="w-full md:w-1/3">
                  {isSubmitted ? (
                    <div className="bg-green-50 p-6 rounded-xl text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check size={24} className="text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold mb-2">Request Sent Successfully!</h3>
                      <p className="text-gray-600 mb-4">
                        Thank you for your interest in {helicopter.name}. Our team will contact you shortly to discuss the details.
                      </p>
                      <button
                        onClick={onClose}
                        className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-6 rounded-xl">
                      <h3 className="text-lg font-bold mb-4">Request This Helicopter</h3>
                      
                      <div className="space-y-4 mb-6">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Charter Price</span>
                          <span className="font-medium">{helicopter.price}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Location</span>
                          <span className="font-medium">{helicopter.location}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-4 flex justify-between">
                          <span className="font-bold">Availability</span>
                          <span className="font-bold text-green-600">Available Now</span>
                        </div>
                      </div>
                      
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Additional Requests (Optional)
                          </label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent resize-none"
                          ></textarea>
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium flex items-center justify-center gap-2"
                        >
                          {isSubmitting ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              <span>Sending...</span>
                            </>
                          ) : (
                            <>
                              <span>Request Booking</span>
                              <ArrowRight size={18} />
                            </>
                          )}
                        </button>
                      </form>
                      
                      <div className="mt-6 text-xs text-gray-500 text-center">
                        By booking, you agree to our <a href="/terms" className="underline">Terms & Conditions</a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default function HelicopterCharter() {
  const { isAuthenticated, user } = useAuth();
  const [helicopters, setHelicopters] = useState<Helicopter[]>([
    {
      id: 1,
      name: 'Airbus ACH160',
      type: 'VIP Helicopter',
      capacity: '6 passengers',
      range: '850 km',
      speed: '280 km/h',
      price: '€5,500/hour',
      location: 'Globally',
      image: 'https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/helicopter/image-112-1024x683.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJoZWxpY29wdGVyL2ltYWdlLTExMi0xMDI0eDY4My5wbmciLCJpYXQiOjE3NDY0Njg2MzIsImV4cCI6MzA0MjQ2ODYzMn0.bQOoPWMGDAh8EeRCHYQFZMiBhkQ1RD9osvUpGVdoy9M'
    },
    {
      id: 2,
      name: 'Bell 429',
      type: 'Corporate Helicopter',
      capacity: '7 passengers',
      range: '760 km',
      speed: '260 km/h',
      price: '€4,800/hour',
      location: 'Globally',
      image: 'https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/helicopter/bell%20429.jpg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJoZWxpY29wdGVyL2JlbGwgNDI5LmpwZyIsImlhdCI6MTc0NjQ2ODcyMCwiZXhwIjoxNDcwNjQ2ODcyMH0.sSNF0T_wCTmLN7WatU2H-1WGkhuOS6Xb4oE0TeRfxss'
    },
    {
      id: 3,
      name: 'Leonardo AW139',
      type: 'Executive Helicopter',
      capacity: '12 passengers',
      range: '1,060 km',
      speed: '310 km/h',
      price: '€7,200/hour',
      location: 'Globally',
      image: 'https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/helicopter/body_private_executive.jpg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJoZWxpY29wdGVyL2JvZHlfcHJpdmF0ZV9leGVjdXRpdmUuanBnIiwiaWF0IjoxNzQ2NDY4Nzg5LCJleHAiOjE0NzA2NDY4Nzg5fQ.UkCfAGCHBJCq4o889zrX6AV1-DKBGchTOz-QMVncz7A'
    },
    {
      id: 4,
      name: 'Airbus ACH145',
      type: 'VIP Helicopter',
      capacity: '9 passengers',
      range: '740 km',
      speed: '250 km/h',
      price: '€6,200/hour',
      location: 'Globally',
      image: 'https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/helicopter/Airbus%20ACH145.webp?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJoZWxpY29wdGVyL0FpcmJ1cyBBQ0gxNDUud2VicCIsImlhdCI6MTc0NjQ2ODg3MSwiZXhwIjozMDQyNDY4ODcxfQ.zB2ozFay8YucgpzAFbnx5qlEf_1mBy8ogISBEhQT8Og'
    },
    {
      id: 5,
      name: 'Bell 505',
      type: 'Light Helicopter',
      capacity: '4 passengers',
      range: '560 km',
      speed: '230 km/h',
      price: '€3,200/hour',
      location: 'Globally',
      image: 'https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/helicopter/Bell%20505.avif?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJoZWxpY29wdGVyL0JlbGwgNTA1LmF2aWYiLCJpYXQiOjE3NDY0Njg5MjUsImV4cCI6MTQ3MDY0Njg5MjV9.4CmyfmdbFURDfV-Kb3VOjkIjkXMzWQlvcQaU4bUDjWE'
    },
    {
      id: 6,
      name: 'Sikorsky S-76',
      type: 'Executive Helicopter',
      capacity: '8 passengers',
      range: '820 km',
      speed: '270 km/h',
      price: '€5,800/hour',
      location: 'Globally',
      image: 'https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/helicopter/HF-Interior-SikorskyS76-1024x709.jpg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJoZWxpY29wdGVyL0hGLUludGVyaW9yLVNpa29yc2t5Uzc2LTEwMjR4NzA5LmpwZyIsImlhdCI6MTc0NjQ2ODk4NCwiZXhwIjoxNDcwNjQ2ODk4NH0.51ps2j3aopaCGNljX3Fuh4QgA8tp0PEtNgTLfRterps'
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [selectedHelicopter, setSelectedHelicopter] = useState<Helicopter | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminStatus();
    // In a real application, you would fetch helicopters from Supabase here
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user) return;
    
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (!error && userData) {
        setIsAdmin(userData.is_admin);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const handleHelicopterClick = (helicopter: Helicopter) => {
    setSelectedHelicopter(helicopter);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedHelicopter(null);
  };

  const filteredHelicopters = helicopters.filter(helicopter => {
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        helicopter.name.toLowerCase().includes(search) ||
        helicopter.type.toLowerCase().includes(search) ||
        helicopter.location.toLowerCase().includes(search)
      );
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      return helicopter.type.toLowerCase().includes(filterType.toLowerCase());
    }
    
    return true;
  });

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between py-4 px-6">
            <div className="flex items-center gap-8">
              <Logo />
              <NavigationMenu />
            </div>
            <div className="flex items-center gap-4">
              <WalletMenu onShowDashboard={() => {}} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Helicopter Charter</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the ultimate in flexibility and convenience with our helicopter charter services. Perfect for short-distance travel and city-to-city transfers.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="max-w-md mx-auto">
              <div className="relative">
                <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, type, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent shadow-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Admin Add Button */}
          {isAdmin && (
            <div className="mb-6 flex justify-end">
              <a 
                href="/admin/helicopters" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <Plus size={18} />
                <span>Add New Helicopter</span>
              </a>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'all' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Helicopters
            </button>
            <button
              onClick={() => setFilterType('vip')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'vip' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              VIP Helicopters
            </button>
            <button
              onClick={() => setFilterType('executive')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'executive' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Executive Helicopters
            </button>
            <button
              onClick={() => setFilterType('light')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'light' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Light Helicopters
            </button>
          </div>

          {/* Helicopters Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
            </div>
          ) : filteredHelicopters.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredHelicopters.map((helicopter) => (
                <div 
                  key={helicopter.id} 
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow border border-gray-100 cursor-pointer group"
                  onClick={() => handleHelicopterClick(helicopter)}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={helicopter.image} 
                      alt={helicopter.name} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      {helicopter.type}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold mb-2">{helicopter.name}</h3>
                    <div className="flex items-center text-gray-600 mb-3">
                      <MapPin size={16} className="mr-1" />
                      <span className="text-sm">{helicopter.location}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 mb-4">
                      <div className="flex items-center text-xs bg-gray-100 px-2 py-1 rounded-full">
                        <Users size={14} className="mr-1" />
                        <span>{helicopter.capacity}</span>
                      </div>
                      <div className="flex items-center text-xs bg-gray-100 px-2 py-1 rounded-full">
                        <Clock size={14} className="mr-1" />
                        <span>{helicopter.speed}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-lg font-bold">
                        {helicopter.price}
                      </div>
                      <button className="text-xs bg-black text-white px-3 py-1.5 rounded-lg font-medium hover:bg-gray-800 transition-colors">
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info size={24} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">No helicopters found</h3>
              <p className="text-gray-500 mt-2">
                No helicopters match your search criteria. Please try different search terms or filters.
              </p>
            </div>
          )}

          {/* CTA Section */}
          <div className="bg-black text-white p-12 rounded-2xl text-center mt-16">
            <h2 className="text-3xl font-bold mb-4">Ready to Experience Helicopter Travel?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Contact us today to book your helicopter charter or learn more about our services.
            </p>
            <a 
              href="mailto:bookings@privatecharterx.com" 
              className="inline-block bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              Request a Quote
            </a>
          </div>
        </div>
      </main>

      <Footer />

      {/* Helicopter Detail Modal */}
      {showModal && selectedHelicopter && (
        <HelicopterDetailModal helicopter={selectedHelicopter} onClose={closeModal} />
      )}
    </div>
  );
}